// ---------------------------------------------------------------------------
// alert-card — expiring/actionable items, one factory → four descriptors
// ---------------------------------------------------------------------------
//
// Each alert card fetches /dashboard/alerts once, reads its slice, and renders a
// list with urgency badges (classifyUrgency, using the response asOf) + a
// per-card "All good" empty state. Rows navigate to the destination list page.
// The events card additionally renders ignore/disable buttons (POST RPC →
// refetch on success); those buttons stopPropagation so they don't trigger row
// navigation, and are disabled in-flight to avoid a double-fire 404.

import { createElement } from '../../../core/dom/helpers.js';
import { classifyUrgency } from '../urgency.js';
import { isAbortError } from '../../../core/http/is-abort-error.js';

/**
 * @typedef {import('../../../contracts/card-grid.js').CardDescriptor} CardDescriptor
 * @typedef {import('../../../contracts/card-grid.js').CardHandle} CardHandle
 * @typedef {import('../../../contracts/dashboard.js').DashboardCardContext} CardContext
 * @typedef {import('../../../contracts/dashboard.js').AlertItem} AlertItem
 * @typedef {import('../../../contracts/dashboard.js').AlertsResponse} AlertsResponse
 * @typedef {CardDescriptor & { render: (ctx: CardContext) => CardHandle }} DashboardCard
 */

/**
 * @typedef {Object} AlertCardConfig
 * @property {string} id
 * @property {string} title
 * @property {'banners'|'catalog'|'advertorials'|'events'} kind
 * @property {keyof AlertsResponse} sliceKey - which array in the response.
 * @property {string} [listPath] - route to navigate to on row click.
 */

/**
 * Build one alert card descriptor.
 *
 * @param {AlertCardConfig} config
 * @returns {DashboardCard}
 */
export function createAlertCard(config) {
  return {
    id: config.id,
    title: config.title,
    render(ctx) {
      return mountAlertCard(config, ctx);
    },
  };
}

/**
 * @param {AlertCardConfig} config
 * @param {CardContext} ctx
 * @returns {{ el: HTMLElement, destroy: () => void }}
 */
function mountAlertCard(config, ctx) {
  const el = createElement('div', { class: 'dashboard-alert' });
  let destroyed = false;
  let loadSeq = 0;
  /** @type {Set<() => void>} */
  const cleanups = new Set();

  /** @param {boolean} busy */
  function setBusy(busy) {
    el.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /** @param {string} text @param {string} modifier */
  function renderMessage(text, modifier) {
    el.replaceChildren(
      createElement('p', { class: `dashboard-alert__message dashboard-alert__message--${modifier}` }, [text]),
    );
  }

  /** @param {AlertItem[]} items @param {string} asOf */
  function renderList(items, asOf) {
    for (const c of cleanups) c();
    cleanups.clear();

    if (!items.length) {
      renderMessage('All good — nothing here needs attention.', 'ok');
      return;
    }

    const list = createElement('ul', { class: 'dashboard-alert__list' });
    for (const item of items) {
      list.appendChild(renderRow(item, asOf));
    }
    el.replaceChildren(list);
  }

  /** @param {AlertItem} item @param {string} asOf @returns {HTMLElement} */
  function renderRow(item, asOf) {
    const { label, variant } = classifyUrgency(item, asOf, config.kind);

    const titleParts = [item.title];
    if (item.country) titleParts.push(` · ${item.country}`);
    const titleText = titleParts.join('');

    const main = createElement('span', { class: 'dashboard-alert__row-title' }, [titleText]);

    const badge = createElement('span', {
      class: `dashboard-alert__badge dashboard-alert__badge--${variant}`,
    }, [label]);

    const meta = createElement('span', { class: 'dashboard-alert__row-meta' }, [
      item.expiresAt ? `expires ${item.expiresAt}` : '',
    ]);

    /** @type {(string|Node)[]} */
    const rowChildren = [main, meta, badge];

    // Events card: ignore + disable actions.
    if (config.kind === 'events') {
      rowChildren.push(renderEventActions(item));
    }

    const row = createElement('li', { class: 'dashboard-alert__row' }, rowChildren);

    // Whole-row navigation (events row uses buttons that stopPropagation).
    //
    // The interactive role/tabindex/handlers live on an inner wrapper, NOT on
    // the <li> itself: putting role="link" on the <li> strips its implicit
    // `listitem` role, which makes the parent <ul> an invalid list (WCAG 1.3.1 /
    // axe `list`). The <li> stays a plain list item; the wrapper is the link.
    if (config.listPath) {
      const link = createElement('div', {
        class: 'dashboard-alert__row-link',
        role: 'link',
        tabindex: '0',
      });
      // Move the row's rendered children into the link wrapper. The wrapper is
      // the interactive element (role="link" + handlers + hover/focus styling);
      // the <li> stays a plain list item so the <ul> is a valid list. The
      // `--link` marker on the <li> keys the styling that targets the wrapper.
      link.append(...row.childNodes);
      row.appendChild(link);
      row.classList.add('dashboard-alert__row--link');

      const go = () => ctx.navigate(/** @type {string} */ (config.listPath));
      /** @param {Event} e */
      const onClick = (e) => {
        // Navigate on a click anywhere in the row EXCEPT on an interactive
        // control (the event-action buttons today; future-proofed for links/
        // inputs). The wrapper itself carries role="link", so closest('[role=link]')
        // matches it — only bail when the match is a DISTINCT descendant.
        if (!(e.target instanceof Element)) return;
        const hit = e.target.closest('button,a,input,select,textarea,[role="button"],[role="link"]');
        if (hit && hit !== link) return;
        go();
      };
      /** @param {KeyboardEvent} e */
      const onKey = (e) => {
        // Only the link wrapper itself activates navigation — a keypress on a
        // focused child button (ignore/disable) must not bubble into navigation.
        if (e.target !== link) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      };
      link.addEventListener('click', onClick);
      link.addEventListener('keydown', onKey);
      cleanups.add(() => {
        link.removeEventListener('click', onClick);
        link.removeEventListener('keydown', onKey);
      });
    }

    return row;
  }

  /** @param {AlertItem} item @returns {HTMLElement} */
  function renderEventActions(item) {
    const ignoreBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button',
      class: 'dashboard-alert__action',
      'aria-label': `Leave "${item.title}" without translation`,
    }, ['ignore']));

    const disableBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button',
      class: 'dashboard-alert__action dashboard-alert__action--danger',
      'aria-label': `Disable "${item.title}"`,
    }, ['disable']));

    const actions = createElement('span', { class: 'dashboard-alert__actions' }, [ignoreBtn, disableBtn]);

    /** @param {HTMLButtonElement} btn @param {string} action */
    function wire(btn, action) {
      /** @param {Event} e */
      const onClick = (e) => {
        e.stopPropagation(); // don't trigger row navigation
        // Per-ROW (not per-button) in-flight guard: clicking ignore then disable
        // on the same row would otherwise fire a second RPC that 404s on the
        // already-removed pid. Both buttons in this row are disabled together.
        runEventAction(action, item, [ignoreBtn, disableBtn]);
      };
      btn.addEventListener('click', onClick);
      cleanups.add(() => btn.removeEventListener('click', onClick));
    }
    wire(ignoreBtn, 'changeLastChangeExpoTranslate');
    wire(disableBtn, 'expoTranslateDisable');

    return actions;
  }

  /** @param {string} action @param {AlertItem} item @param {HTMLButtonElement[]} rowButtons */
  function runEventAction(action, item, rowButtons) {
    if (rowButtons.some((b) => b.disabled)) return; // row already has an action in flight
    for (const b of rowButtons) b.disabled = true;
    ctx.logger.info('Dashboard event action invoked', { data: { action, pid: item.pid } });
    ctx.apiClient.post(`/dashboard/events/${action}`, { pid: item.pid }, { signal: ctx.signal })
      .then(() => {
        if (destroyed) return;
        ctx.logger.info('Dashboard event action success', { data: { action, pid: item.pid } });
        load(); // refetch so the row disappears (backend mutated the fixture)
      })
      .catch((/** @type {unknown} */ err) => {
        if (isAbortError(err) || destroyed) return;
        for (const b of rowButtons) b.disabled = false;
        ctx.logger.error('Dashboard event action failed', { data: { action, pid: item.pid } });
        ctx.eventBus.emit('toast.message.requested', { message: 'Action failed', type: 'error' });
      });
  }

  function load() {
    const mine = ++loadSeq;
    setBusy(true);
    ctx.apiClient.get('/dashboard/alerts', { signal: ctx.signal })
      .then((/** @type {unknown} */ raw) => {
        // Freshness guard: a stale response (older load resolving last) must not
        // repaint over a newer one. Applied in both then + catch.
        if (destroyed || mine !== loadSeq) return;
        setBusy(false);
        const data = /** @type {AlertsResponse} */ (raw) || {};
        const items = /** @type {AlertItem[]} */ (
          Array.isArray(/** @type {any} */ (data)[config.sliceKey]) ? /** @type {any} */ (data)[config.sliceKey] : []
        );
        renderList(items, String(data.asOf ?? ''));
      })
      .catch((/** @type {unknown} */ err) => {
        if (isAbortError(err) || destroyed || mine !== loadSeq) return;
        setBusy(false);
        ctx.logger.error('Dashboard alerts load failed', { data: { kind: config.kind } });
        renderMessage('Couldn’t load this card.', 'error');
        ctx.eventBus.emit('toast.message.requested', { message: 'Failed to load dashboard alerts', type: 'error' });
      });
  }

  renderMessage('Loading…', 'loading');
  load();

  return {
    el,
    destroy() {
      destroyed = true;
      for (const c of cleanups) c();
      cleanups.clear();
    },
  };
}
